import { AccountPlan } from '@/constants/enum';
import { usePlanDisplayName } from '@/hooks/usePlanDisplayName';
import { generateDiscountPlan } from './helpers';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import { useToggle } from '@/hooks';
import { useGetUserPlanQuery, useUpdateDiscountCodeMutation } from '@/redux/api/api.caller';
import { handleToast } from '@/redux/features/common.slice';
import { DiscountType, IUpdateDiscountCode } from '@/types/account';
import { Button, Icon, Modal, Text, TextField, Tooltip } from '@shopify/polaris';
import { DiscountIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import { forwardRef, memo, Ref, useCallback, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';
import { StyledDiscountCode } from './styled';

interface DiscountCodeProps {
  annuallyDiscountContent: string;
}

export interface DiscountCodeRef {
  toggleModal: () => void;
  setDiscount: React.Dispatch<React.SetStateAction<{ code: string; error: string }>>;
  handleApplyDiscountCode: () => void;
}

const DiscountCode = ({ annuallyDiscountContent }: DiscountCodeProps, ref: Ref<DiscountCodeRef>) => {
  const dispatch = useDispatch();
  const { data } = useGetUserPlanQuery({});
  const { getPlanName } = usePlanDisplayName();
  const [updateDiscountCode, { isLoading }] = useUpdateDiscountCodeMutation();

  const [isOpenModal, toggleModal] = useToggle<boolean>(false);
  const [discount, setDiscount] = useState<{ code: string; error: string }>({
    code: '',
    error: '',
  });

  const handleChange = (value: string) => {
    setDiscount({
      code: value,
      error: '',
    });
  };

  const handleApplyDiscountCode = useCallback(() => {
    const payload: IUpdateDiscountCode = {
      discountCode: discount.code,
      plan: data ? data.data.plan : AccountPlan.Starter,
    };
    updateDiscountCode(payload).then((data) => {
      if ('data' in data && data.data.state === 1) {
        toggleModal();
        setDiscount({ code: '', error: '' });
        dispatch(handleToast(handleToastMutation(data)));
        return;
      }
      setDiscount({
        ...discount,
        error: getErrorFromAPI(data),
      });
    });
  }, [discount, data, dispatch, updateDiscountCode, toggleModal]);

  const renderAppliedDiscount = () => {
    if (data && data.data.discount.length && data.data.discountPlanApply.length) {
      const { discount, discountPlanApply } = data.data;

      return (
        <div className="discount-code">
          Applied discount:
          {discount.map(({ code, value, type, expireTime, startTime }, index) => {
            const isFeatureType = type === DiscountType.Feature;
            return (
              <Tooltip
                key={index}
                content={
                  isFeatureType ? (
                    <>
                      Validation date is on <strong>{dayjs(startTime).format('MMM DD, YYYY')}</strong>. End date is on{' '}
                      <strong>{dayjs(expireTime).format('MMM DD, YYYY')}</strong>
                    </>
                  ) : (
                    `Enjoy your ${
                      type === DiscountType.Fixed ? `$${value}` : `${Math.floor(value || 0)}%`
                    } discount for ${generateDiscountPlan(discountPlanApply, getPlanName)} monthly plans when upgrading. `
                  )
                }
              >
                <div className="discount-badge">
                  <Icon source={DiscountIcon} tone="base" />
                  <span className="discount-text">{code}</span>
                </div>
              </Tooltip>
            );
          })}
        </div>
      );
    }
    return <></>;
  };

  useImperativeHandle(
    ref,
    () => ({
      toggleModal,
      setDiscount,
      handleApplyDiscountCode,
    }),
    [toggleModal, setDiscount, handleApplyDiscountCode],
  );

  return (
    <StyledDiscountCode>
      <div className="discount-code-btn">
        <Button onClick={toggleModal} variant="plain">
          Apply your discount code
        </Button>
      </div>
      {renderAppliedDiscount()}
      <Modal
        open={isOpenModal}
        onClose={toggleModal}
        title="Apply discount code"
        primaryAction={{
          content: 'Apply',
          onAction: handleApplyDiscountCode,
          disabled: discount.code === '',
          loading: isLoading,
        }}
      >
        <Modal.Section>
          <TextField
            autoComplete="off"
            label="Discount code:"
            helpText={
              <>
                <b>Note: </b>
                <Text as="span">
                  {`Discount code does not apply to yearly plans ${
                    annuallyDiscountContent
                      ? `(yearly plans
                 ${annuallyDiscountContent} discount by default)`
                      : ''
                  }`}
                </Text>
              </>
            }
            value={discount.code}
            onChange={handleChange}
            error={discount.error}
          />
        </Modal.Section>
      </Modal>
    </StyledDiscountCode>
  );
};

export default memo(forwardRef<DiscountCodeRef, DiscountCodeProps>(DiscountCode));
